import { Injectable } from '@angular/core';
import {
    BatteryService,
    CraftService,
    PersonService
} from '@flyfreely-portal-ui/flyfreely';
import {
    SelectedOrganisation,
    WorkspaceStateService
} from '@flyfreely-portal-ui/workspace';
import { firstValueFrom } from 'rxjs';

const PEOPLE_PAGE_SIZE = 20;

@Injectable()
export class RichTextEditorConfigService {
    private personnelPage = 0;
    private personnelTotalCount: number | null = null;
    private currentSearchTerm = '';
    private currentOrganisationId: number | null = null;

    constructor(
        private workspaceStateService: WorkspaceStateService,
        private personService: PersonService,
        private craftService: CraftService,
        private batteryService: BatteryService
    ) {
        this.personService.change$.subscribe(() => {
            this.resetPeopleState();
        });
    }

    resetPeopleState() {
        this.personnelPage = 0;
        this.personnelTotalCount = null;
        this.currentSearchTerm = '';
        this.currentOrganisationId = null;
    }

    private async getOrganisationId(): Promise<number | null> {
        const organisation = await firstValueFrom<SelectedOrganisation>(
            this.workspaceStateService.currentOrganisation$
        );
        if (organisation.type !== 'organisation_loaded') {
            return null;
        }
        return organisation.organisation.id;
    }

    async suggestPeople(searchTerm: string) {
        const organisationId = await this.getOrganisationId();
        if (organisationId == null) return [];

        // Reset pagination when search term or org changes
        if (
            searchTerm !== this.currentSearchTerm ||
            organisationId !== this.currentOrganisationId
        ) {
            this.personnelPage = 0;
            this.personnelTotalCount = null;
            this.currentSearchTerm = searchTerm;
            this.currentOrganisationId = organisationId;
        }

        const result = await firstValueFrom(
            this.personService.findPersonnelGraphQL(
                organisationId,
                this.personnelPage,
                PEOPLE_PAGE_SIZE,
                searchTerm ? { textSearch: searchTerm } : {}
            )
        );

        this.personnelTotalCount = result.count;

        return result.results.map(p => ({
            id: p.id,
            value: `${p.firstName} ${p.lastName}`
        }));
    }

    async suggestPeopleLazyLoad(searchTerm: string) {
        const organisationId = await this.getOrganisationId();
        if (organisationId == null) return { items: [], isLastPage: true };

        const loadedCount = (this.personnelPage + 1) * PEOPLE_PAGE_SIZE;
        if (
            this.personnelTotalCount != null &&
            loadedCount >= this.personnelTotalCount
        ) {
            return { items: [], isLastPage: true };
        }

        this.personnelPage += 1;

        const result = await firstValueFrom(
            this.personService.findPersonnelGraphQL(
                organisationId,
                this.personnelPage,
                PEOPLE_PAGE_SIZE,
                searchTerm ? { textSearch: searchTerm } : {}
            )
        );

        this.personnelTotalCount = result.count;
        const totalLoaded = (this.personnelPage + 1) * PEOPLE_PAGE_SIZE;
        const isLastPage = totalLoaded >= result.count;

        return {
            items: result.results.map(p => ({
                id: p.id,
                value: `${p.firstName} ${p.lastName}`
            })),
            isLastPage
        };
    }

    async suggestRpa(searchTerm: string) {
        const organisation = await firstValueFrom<SelectedOrganisation>(
            this.workspaceStateService.currentOrganisation$
        );

        if (organisation.type !== 'organisation_loaded') {
            return [];
        }

        const organisationId = organisation.organisation.id;

        const allRpa = await firstValueFrom(
            this.craftService.findCrafts(organisationId)
        );

        const rpaOptions = allRpa.map(rpa => ({
            id: rpa.id,
            value: rpa.nickname
        }));

        return rpaOptions.filter(rpa =>
            rpa.value.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }

    async suggestBattery(searchTerm: string) {
        const organisation = await firstValueFrom<SelectedOrganisation>(
            this.workspaceStateService.currentOrganisation$
        );

        if (organisation.type !== 'organisation_loaded') {
            return [];
        }

        const organisationId = organisation.organisation.id;

        const allBatteries = await firstValueFrom(
            this.batteryService.findBatteries(organisationId)
        );

        const batteryOptions = allBatteries.map(battery => ({
            id: battery.id,
            value: battery.name
        }));

        return batteryOptions.filter(battery =>
            battery.value.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }
}
